'use client'

import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { apiResponseHandler, queryKeys } from '@/shared/api/react-query'
import services from '../services'
import {
  UploadProjectFileChunkRequest,
  UploadProjectFileChunkResponse,
  UploadProjectFileChunkSuccessData,
  UploadStatus,
} from '../services/upload-project-file'

interface UseUploadProjectFileOptions {
  chunkSize?: number
  onProgress?: (progress: number) => void
  onComplete?: (url: string, uuid: string, data: UploadProjectFileChunkSuccessData) => void | Promise<void>
  onError?: (error: Error) => void
}

const useUploadProjectFile = (options: UseUploadProjectFileOptions = {}) => {
  const { chunkSize = 1024 * 1024, onProgress, onComplete, onError } = options
  const [uploadProgress, setUploadProgress] = useState(0)
  const [uploadSuccess, setUploadSuccess] = useState(false)

  const uploadChunkMutation = useMutation({
    mutationKey: [queryKeys((a, m, p) => a.project.uploadProjectFile + m.post)],
    mutationFn: async (data: UploadProjectFileChunkRequest) => {
      return apiResponseHandler(await services.uploadProjectFile(data))
    },
  })

  const uploadFile = async (file: File, projectId: string) => {
    try {
      const totalChunks = Math.ceil(file.size / chunkSize)
      let chunkIndex = 0
      setUploadProgress(0)
      setUploadSuccess(false)

      const sendChunk = async (): Promise<void> => {
        const start = chunkIndex * chunkSize
        const end = Math.min(start + chunkSize, file.size)
        const chunk = file.slice(start, end)

        const chunkData: UploadProjectFileChunkRequest = {
          projectId,
          file: chunk,
          fileName: file.name,
          chunkIndex,
          totalChunks,
          totalSize: file.size,
        }

        const serverData = (await uploadChunkMutation.mutateAsync(chunkData)) as UploadProjectFileChunkResponse

        chunkIndex++
        const progress = Math.round((chunkIndex / totalChunks) * 100)
        setUploadProgress(progress)
        onProgress?.(progress)

        if (serverData.status === UploadStatus.SUCCESS) {
          const successData = serverData.data as UploadProjectFileChunkSuccessData
          setUploadSuccess(true)
          await onComplete?.(successData.url, successData.uuid, successData)
        } else if (chunkIndex < totalChunks) {
          await sendChunk()
        }
      }

      await sendChunk()
    } catch (error) {
      onError?.(error as Error)
      throw error
    }
  }

  return {
    uploadFile,
    uploadProgress,
    uploadSuccess,
    isUploading: uploadChunkMutation.isPending,
    error: uploadChunkMutation.error,
    reset: () => {
      setUploadProgress(0)
      setUploadSuccess(false)
      uploadChunkMutation.reset()
    },
  }
}

export default useUploadProjectFile
